import AppLayout from '@/layout/AppLayout.vue';
import LandingLayout from '@/layout/LandingLayout.vue';
import { createRouter, createWebHistory, RouterView } from 'vue-router';
import { isAuthenticated } from '@/auth/authUtils';

const routes = [
    {
        path: '/',
        component: AppLayout,
        children: [
            // Dashboard
            {
                path: '',
                name: 'notification-dashboard',
                meta: {
                    breadcrumb: ['Dashboard']
                },
                component: () => import('@/views/dashboard/NotificationDashboard.vue')
            },
            {
                path: 'campaign-responses/needs-review',
                name: 'campaign-responses-needs-review',
                meta: {
                    breadcrumb: ['Campaign Responses', 'Needs Review']
                },
                component: () => import('@/views/campaigns/NeedsReviewQueue.vue')
            },
            // Document Management Routes
            {
                path: 'documents',
                name: 'documents',
                meta: {
                    breadcrumb: ['Documents']
                },
                component: () => import('@/components/dashboard/Documents.vue')
            },
            {
                path: 'documents/upload',
                name: 'document-upload',
                meta: {
                    breadcrumb: ['Documents', 'Upload']
                },
                component: () => import('@/views/apps/document-upload/DocumentUpload.vue')
            },
            {
                path: 'documents/upload-with-inbound',
                name: 'document-upload-with-inbound',
                meta: {
                    breadcrumb: ['Documents', 'Upload with Inbound']
                },
                component: () => import('@/views/apps/document-upload/DocumentUploadWithInbound.vue')
            },
            {
                path: 'inbounds',
                name: 'inbounds',
                meta: {
                    breadcrumb: ['Inbounds', 'Manage']
                },
                component: () => import('@/views/apps/inbounds/Inbounds.vue')
            },
            // Imazing iPhone export upload
            {
                path: 'imazing/upload',
                name: 'imazing-upload',
                meta: {
                    breadcrumb: ['Imazing', 'Upload']
                },
                component: () => import('@/views/apps/imazing/ImazingUpload.vue')
            },
            // Reactor runs dashboard
            {
                path: 'reactors/runs',
                name: 'reactors-runs',
                meta: {
                    breadcrumb: ['Reactors', 'Runs']
                },
                component: () => import('@/views/apps/reactors/ReactorDashboard.vue')
            },
            // My Calendar: merged CRM case events + user Outlook events
            {
                path: 'calendar',
                name: 'my-calendar',
                meta: {
                    breadcrumb: ['My Calendar']
                },
                component: () => import('@/views/calendar/MyCalendar.vue')
            },
            {
                path: 'court-days',
                name: 'court-days',
                meta: {
                    breadcrumb: ['My Court Days']
                },
                component: () => import('@/views/court-days/CourtDaysLanding.vue')
            },
            {
                path: 'court-days/:date',
                name: 'court-day-workspace',
                meta: {
                    breadcrumb: ['My Court Days', 'Workspace'],
                    remountOnParamChange: 'date'
                },
                component: () => import('@/views/court-days/CourtDayWorkspace.vue'),
                props: true
            },
            {
                path: 'inbounds/:id',
                name: 'inbound-detail',
                meta: {
                    breadcrumb: ['Inbounds', 'Detail']
                },
                component: () => import('@/views/apps/inbounds/Inbound.vue'),
                props: true
            },
            {
                path: 'inbounds/:inboundId/documents/:id',
                name: 'document-detail',
                meta: {
                    breadcrumb: ['Inbounds', 'Document Detail']
                },
                component: () => import('@/components/dashboard/Document.vue'),
                props: true
            },

            // CRM Routes
            {
                path: 'crm',
                component: RouterView,
                meta: {
                    breadcrumb: ['CRM']
                },
                children: [
                    {
                        path: 'providers',
                        name: 'crm-providers',
                        meta: {
                            breadcrumb: ['CRM', 'Providers']
                        },
                        component: () => import('@/views/crm/Providers.vue')
                    },
                    {
                        path: 'cases/:caseNumber',
                        name: 'crm-case-detail',
                        meta: {
                            breadcrumb: ['CRM', 'Cases', 'Case Detail'],
                            // Params-only navigation reuses the component instance; remount when this param changes
                            remountOnParamChange: 'caseNumber'
                        },
                        component: () => import('@/views/crm/Case.vue'),
                        props: true
                    },
                    {
                        path: 'contacts',
                        name: 'crm-contacts',
                        meta: {
                            breadcrumb: ['CRM', 'Contacts']
                        },
                        component: () => import('@/views/crm/Contacts.vue')
                    },
                    {
                        path: 'communications',
                        name: 'crm-communications',
                        meta: {
                            breadcrumb: ['CRM', 'Communications']
                        },
                        component: () => import('@/views/crm/CommunicationsSearch.vue')
                    }
                ]
            },

            // Mail Routes
            {
                path: 'mail',
                component: RouterView,
                meta: {
                    breadcrumb: ['Mail']
                },
                children: [
                    {
                        path: 'messages',
                        name: 'mail-messages',
                        meta: {
                            breadcrumb: ['Mail', 'Messages']
                        },
                        component: () => import('@/views/mail/MailPage.vue')
                    }
                ]
            },

            // Provider Management Routes
            {
                path: 'providers',
                component: RouterView,
                meta: {
                    breadcrumb: ['Providers']
                },
                children: [
                    {
                        path: 'fax',
                        name: 'fax-providers',
                        meta: {
                            breadcrumb: ['Providers', 'Fax'],
                            requiresAuth: true
                        },
                        component: () => import('@/views/fax/FaxProviders.vue')
                    },
                    {
                        path: 'scan',
                        name: 'scan-providers',
                        meta: {
                            breadcrumb: ['Providers', 'Scan'],
                            requiresAuth: true
                        },
                        component: () => import('@/views/scan/ScanProviders.vue')
                    },
                    {
                        path: 'mail',
                        name: 'mail-providers',
                        meta: {
                            breadcrumb: ['Providers', 'Mail']
                        },
                        component: () => import('@/views/mail/Providers.vue')
                    },
                    {
                        path: 'email',
                        component: () => import('@/views/apps/emails/Index.vue'),
                        children: [
                            {
                                path: '',
                                name: 'email-providers',
                                meta: {
                                    breadcrumb: ['Providers', 'Email']
                                },
                                component: () => import('@/views/apps/emails/List.vue')
                            },
                            {
                                path: 'view/:id',
                                name: 'email-provider-detail',
                                meta: {
                                    breadcrumb: ['Providers', 'Email', 'Details']
                                },
                                component: () => import('@/views/apps/emails/Detail.vue')
                            },
                            {
                                path: 'add',
                                name: 'email-provider-add',
                                meta: {
                                    breadcrumb: ['Providers', 'Email', 'Add']
                                },
                                component: () => import('@/views/apps/emails/Form.vue')
                            },
                            {
                                path: 'edit/:id',
                                name: 'email-provider-edit',
                                meta: {
                                    breadcrumb: ['Providers', 'Email', 'Edit']
                                },
                                component: () => import('@/views/apps/emails/Form.vue')
                            }
                        ]
                    }
                ]
            },

            // PDF Forms Routes
            {
                path: 'pdf-forms/review/:token',
                name: 'pdf-form-review',
                meta: {
                    breadcrumb: ['PDF Forms', 'Review']
                },
                component: () => import('@/views/pdf-forms/FormReview.vue')
            },

            // Admin Routes (requires admin privileges)
            {
                path: 'admin',
                component: RouterView,
                meta: {
                    breadcrumb: ['Admin'],
                    requiresAdmin: true
                },
                children: [
                    {
                        path: 'crm-backlog',
                        name: 'admin-crm-backlog',
                        meta: {
                            breadcrumb: ['Admin', 'CRM Backlog'],
                            requiresAdmin: true
                        },
                        component: () => import('@/views/admin/CRMBacklog.vue')
                    },
                    {
                        // LAW-1140 Stage 5. Deliberately NOT named anything with
                        // "providers" in it — `crm/Providers.vue` already exists
                        // and is the referral-provider admin, a different thing
                        // entirely. This report is about cases.
                        path: 'missing-subpoenas',
                        name: 'admin-missing-subpoenas',
                        meta: {
                            breadcrumb: ['Admin', 'Missing Subpoenas'],
                            requiresAdmin: true
                        },
                        component: () => import('@/views/admin/MedicalEvidenceGapsReport.vue')
                    },
                    {
                        path: 'notifications',
                        name: 'admin-notifications',
                        meta: {
                            breadcrumb: ['Admin', 'Notifications', 'View'],
                            requiresAdmin: true
                        },
                        component: () => import('@/views/admin/NotificationsView.vue')
                    },
                    {
                        path: 'notifications/send',
                        name: 'admin-notifications-send',
                        meta: {
                            breadcrumb: ['Admin', 'Notifications', 'Send'],
                            requiresAdmin: true
                        },
                        component: () => import('@/views/admin/NotificationsSend.vue')
                    },
                    {
                        path: 'notifications/check-missed',
                        name: 'admin-notifications-check-missed',
                        meta: {
                            breadcrumb: ['Admin', 'Notifications', 'Check Missed'],
                            requiresAdmin: true
                        },
                        component: () => import('@/views/admin/NotificationsCheckMissed.vue')
                    },
                    {
                        path: 'crm-cases',
                        name: 'admin-crm-cases',
                        meta: {
                            breadcrumb: ['Admin', 'CRM', 'Cases'],
                            requiresAdmin: true
                        },
                        component: () => import('@/views/admin/AdminCaseWorkflows.vue')
                    },
                    {
                        path: 'communications',
                        name: 'admin-communications',
                        meta: {
                            breadcrumb: ['Admin', 'Communications'],
                            requiresAdmin: true
                        },
                        component: () => import('@/views/call_tracking/AdminCommunications.vue')
                    },
                    {
                        // Focused WCM check-in setup and rollout operations.
                        // Deliberately not a child of `campaigns/:id` — staff
                        // reach it without knowing a campaign id exists.
                        path: 'wcm-checkins',
                        name: 'admin-wcm-checkins',
                        meta: {
                            breadcrumb: ['Admin', 'WCM Check-ins'],
                            requiresAdmin: true
                        },
                        component: () => import('@/views/admin/campaigns/WcmCheckinSetup.vue')
                    },
                    {
                        path: 'campaigns',
                        name: 'admin-campaigns',
                        meta: {
                            breadcrumb: ['Admin', 'Campaigns'],
                            requiresAdmin: true
                        },
                        component: () => import('@/views/admin/campaigns/CampaignList.vue')
                    },
                    {
                        path: 'campaigns/new',
                        name: 'admin-campaigns-new',
                        meta: {
                            breadcrumb: ['Admin', 'Campaigns', 'New'],
                            requiresAdmin: true
                        },
                        component: () => import('@/views/admin/campaigns/CampaignBuilder.vue')
                    },
                    {
                        path: 'campaigns/:id',
                        name: 'admin-campaigns-dashboard',
                        meta: {
                            breadcrumb: ['Admin', 'Campaigns', 'Dashboard'],
                            requiresAdmin: true
                        },
                        component: () => import('@/views/admin/campaigns/CampaignDashboard.vue')
                    },
                    {
                        path: 'campaigns/:id/edit',
                        name: 'admin-campaigns-edit',
                        meta: {
                            breadcrumb: ['Admin', 'Campaigns', 'Edit'],
                            requiresAdmin: true
                        },
                        component: () => import('@/views/admin/campaigns/CampaignBuilder.vue')
                    }
                ]
            },
            // Ad Management (AI Google Ads) — read-only views
            {
                path: 'ad-management',
                component: RouterView,
                meta: {
                    breadcrumb: ['Ad Management']
                },
                children: [
                    {
                        path: '',
                        name: 'ad-campaign-overview',
                        meta: { breadcrumb: ['Ad Management', 'Overview'] },
                        component: () => import('@/views/ad-management/CampaignOverview.vue')
                    },
                    {
                        path: 'approvals',
                        name: 'ad-pending-approvals',
                        meta: { breadcrumb: ['Ad Management', 'Pending Approvals'] },
                        component: () => import('@/views/ad-management/PendingApprovals.vue')
                    },
                    {
                        path: 'decisions',
                        name: 'ad-decision-log',
                        meta: { breadcrumb: ['Ad Management', 'Decision Log'] },
                        component: () => import('@/views/ad-management/DecisionLogViewer.vue')
                    },
                    {
                        path: 'decisions/:decisionId',
                        name: 'ad-decision-detail',
                        meta: { breadcrumb: ['Ad Management', 'Decision Detail'] },
                        component: () => import('@/views/ad-management/DecisionDetail.vue'),
                        props: true
                    },
                    {
                        path: 'anomalies',
                        name: 'ad-anomalies-list',
                        meta: { breadcrumb: ['Ad Management', 'Anomalies'] },
                        component: () => import('@/views/ad-management/AnomaliesList.vue')
                    },
                    {
                        path: 'anomalies/:anomalyId',
                        name: 'ad-anomaly-detail',
                        meta: { breadcrumb: ['Ad Management', 'Anomaly Detail'] },
                        component: () => import('@/views/ad-management/AnomalyDetail.vue'),
                        props: true
                    },
                    {
                        path: 'brief',
                        name: 'ad-marketing-brief',
                        meta: { breadcrumb: ['Ad Management', 'Marketing Brief'] },
                        component: () => import('@/views/ad-management/MarketingBrief.vue')
                    },
                    {
                        path: 'settings/safeguards',
                        name: 'ad-settings-safeguards',
                        meta: { breadcrumb: ['Ad Management', 'Settings', 'Safeguards'], requiresAdmin: true },
                        component: () => import('@/views/ad-management/SafeguardsSettings.vue')
                    },
                    {
                        path: 'settings/policies',
                        name: 'ad-settings-policies',
                        meta: { breadcrumb: ['Ad Management', 'Settings', 'Policies'], requiresAdmin: true },
                        component: () => import('@/views/ad-management/PoliciesSettings.vue')
                    },
                    {
                        path: 'settings/role-assignments',
                        name: 'ad-settings-role-assignments',
                        meta: { breadcrumb: ['Ad Management', 'Settings', 'Role Assignments'], requiresAdmin: true },
                        component: () => import('@/views/ad-management/RoleAssignmentsSettings.vue')
                    }
                ]
            },

            // Marketing / case-attribution dashboard
            {
                path: 'marketing',
                component: RouterView,
                meta: {
                    breadcrumb: ['Marketing']
                },
                children: [
                    {
                        path: 'dashboard',
                        name: 'marketing-dashboard',
                        meta: {
                            breadcrumb: ['Marketing', 'Dashboard']
                        },
                        component: () => import('@/views/marketing/MarketingDashboard.vue')
                    }
                ]
            },
            {
                path: 'reports',
                component: RouterView,
                meta: {
                    breadcrumb: ['Reports'],
                    requiresAdmin: true
                },
                children: [
                    {
                        path: 'daily',
                        name: 'daily-reports',
                        meta: {
                            breadcrumb: ['Reports', 'Daily Analytics']
                        },
                        component: () => import('@/views/reports/daily.vue')
                    },
                    {
                        path: 'case-assignment',
                        name: 'case-assignment-reports',
                        meta: {
                            breadcrumb: ['Reports', 'Case Assignment']
                        },
                        component: () => import('@/views/reports/CaseAssignment.vue')
                    }
                ]
            },

            // Account Management Routes
            {
                path: 'account',
                component: RouterView,
                meta: {
                    breadcrumb: ['Account']
                },
                children: [
                    {
                        path: 'settings',
                        name: 'account-settings',
                        meta: {
                            breadcrumb: ['Account', 'Settings'],
                            requiresAdmin: true
                        },
                        component: () => import('@/views/account/Settings.vue')
                    }
                ]
            },

            // Member settings: one-time Zoom texting consent (the nexus Zoom OAuth callback redirects here).
            {
                path: 'settings/texting',
                name: 'settings-texting',
                meta: {
                    breadcrumb: ['Settings', 'Texting']
                },
                component: () => import('@/views/settings/TextingSettings.vue')
            },

            // Static Pages
            {
                path: 'pages/empty',
                name: 'empty',
                component: () => import('@/views/pages/Empty.vue')
            },
            {
                path: 'pages/notfound',
                name: 'notfound',
                component: () => import('@/views/pages/NotFound.vue')
            },

            // FAQ Page
            {
                path: 'faq',
                name: 'faq',
                meta: {
                    breadcrumb: ['FAQ']
                },
                component: () => import('@/views/pages/Faq.vue')
            }
        ]
    },

    // Landing/Marketing Pages (public access)
    {
        path: '/landing',
        component: LandingLayout,
        meta: { scrollToTop: true },
        children: [
            {
                path: '',
                name: 'landing',
                component: () => import('@/views/landing/LandingView.vue')
            },
            {
                path: 'about',
                name: 'about',
                component: () => import('@/views/landing/AboutView.vue')
            },
            {
                path: 'pricing',
                name: 'pricing',
                component: () => import('@/views/landing/PricingView.vue')
            },
            {
                path: 'contact',
                name: 'contact',
                component: () => import('@/views/landing/ContactView.vue')
            },
            // Authentication Routes (kept at /landing/* for backward compatibility)
            {
                path: 'login',
                name: 'login',
                component: () => import('@/views/auth/LoginView.vue')
            },
            {
                path: 'register',
                name: 'register',
                component: () => import('@/views/auth/RegisterView.vue')
            },
            {
                path: 'verification',
                name: 'verification',
                component: () => import('@/views/auth/VerificationView.vue')
            },
            {
                path: 'forgot-password',
                name: 'forgot-password',
                component: () => import('@/views/auth/ForgotPassword.vue')
            },
            {
                path: 'new-password',
                name: 'new-password',
                component: () => import('@/views/auth/NewPassword.vue')
            },
            {
                path: 'lock-screen',
                name: 'lock-screen',
                component: () => import('@/views/auth/LockScreen.vue')
            },
            {
                path: 'access',
                name: 'access',
                component: () => import('@/views/pages/AccessDenied.vue')
            },
            {
                path: 'oops',
                name: 'oops',
                component: () => import('@/views/pages/Oops.vue')
            }
        ]
    },

    // 404 catch-all route
    {
        path: '/:pathMatch(.*)*',
        name: 'not-found',
        component: () => import('@/views/pages/NotFound.vue')
    }
];

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior() {
        return { left: 0, top: 0 };
    }
});

// Navigation guard to protect routes
router.beforeEach(async (to, from, next) => {
    // Public routes that don't require authentication
    const publicRoutes = [
        '/landing', // All landing page routes are public
        '/auth' // Legacy auth routes if any
    ];
    const isPublicRoute = publicRoutes.some((route) => to.path.startsWith(route));

    // If it's a public route, allow access
    if (isPublicRoute) {
        return next();
    }

    // For protected routes, check authentication
    const isLoggedIn = await isAuthenticated();

    if (isLoggedIn) {
        // Check if route requires admin access
        if (to.meta.requiresAdmin) {
            // TODO: Implement admin check
            // const isAdmin = await checkAdminAccess();
            // if (!isAdmin) {
            //     return next({ name: 'access-denied' });
            // }
        }

        // User is authenticated, proceed to route
        next();
    } else {
        // User is not logged in, redirect to login page
        next({ path: '/landing/login', query: { redirect: to.fullPath } });
    }
});

let reloadAttempts = 0;
router.onError((error, to) => {
    if (reloadAttempts < 1 && (error.message.includes('Failed to fetch dynamically imported module') || error.message.includes('Importing a module script failed'))) {
        reloadAttempts++;
        // Use cache-busting param to ensure fresh assets are fetched
        const url = new URL(to.fullPath, window.location.origin);
        url.searchParams.set('_reload', Date.now().toString());
        window.location.href = url.pathname + url.search;
    }
});

export default router;
